/**
 * Màn đăng nhập của web quầy, và màn đổi mã bắt buộc đi kèm.
 *
 * Ba quyết định về giao diện, đều vì người dùng là lễ tân đang đứng ở quầy:
 *   1. **Một hành động chính**: nút "Vào làm việc". Không có "Đăng ký", không có "Đăng nhập bằng…".
 *   2. **Mã tiệm điền sẵn** từ `?tiem=` trong đường dẫn, hoặc từ lần đăng nhập trước.
 *   2b. **Máy nhớ người vừa vào** (khách phản ánh 23/09 "đăng nhập rất phức tạp"): lần sau chỉ còn
 *      một ô mã. Lần đầu: số điện thoại + mã; ô mã tiệm chỉ hiện khi chưa biết tiệm nào, và gõ
 *      kiểu gì cũng được ("Anstar Hair Salon", dán cả đường dẫn).
 *   3. **Bàn phím số** cho mã 6 số (`inputMode="numeric"`), nhưng ô vẫn là `password` để người
 *      đứng cạnh không đọc được.
 *
 * Trên tablet có thêm khối thương hiệu bên trái: `ui-critic` vòng 0 chấm màn cũ 5,5/10 vì "một cột
 * 336px giữa nền xám trống, không có gì cho biết đây là phần mềm gì".
 *
 * Màn Đăng nhập khi máy chưa nhớ người chia hai vùng (chủ dự án 18/10 tối, kế hoạch `gioi-thieu-sapa-man-dang-nhap.md`
 * mục 12): VÙNG CHÍNH (form + dải bản mới + nút "Xem SAPA làm được gì", cao một màn hình) và VÙNG PHỤ riêng ở dưới
 * ("Vì sao chọn SAPA?" + giá + thông tin công ty + nút "Về đăng nhập"). Hai nút NHẢY thẳng giữa hai vùng, không cuộn
 * mượt. Mở `/#gioi-thieu` thì nhảy tới vùng phụ. Nút "Vào làm việc" vẫn ở khung nhìn đầu.
 */

import {
  CAU_CAN_DONG_Y_DIEU_KHOAN,
  DUONG_DAN_DIEU_KHOAN_CUA_HANG,
  type NganhTiem,
  nganhDeChon,
  TEN_NGANH,
  type TraLoiDangKy,
} from '@sapa/shared'
import {
  BANG_GIA_SAPA,
  SAPA_DANG_MIEN_PHI,
  thangDuocDungKhiDongNam,
  tienDongNamVnd,
} from '@sapa/shared/gia-sapa'
import {
  Building2,
  CalendarCheck2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  KeyRound,
  MapPin,
  MessageCircle,
  Phone,
  ReceiptText,
  Store,
  ToggleRight,
  UserRound,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AnhQrSapa } from './AnhQrSapa.tsx'
import {
  ApiFailure,
  auth,
  duongDanMayChu,
  type HoTro,
  hienSdt,
  type KetQuaDangNhap,
  luuToken,
  type TiemDatMa,
  type TiemDeChon,
  tienVnd,
} from './api.ts'
import { chuanHoaMaTiem, type DaNho, docNho, ghiNho, quenNho } from './dang-nhap-nho.ts'
import { FormDoiMa } from './FormDoiMa.tsx'
import {
  ghiVuaMoTiem,
  laLoiSdtMayChu,
  loiMaLechSom,
  loiMaMoTiem,
  lyDoChuaTiep,
  sdtMoTiemHopLe,
} from './mo-tiem-ma.ts'
import { E } from './text/mo-tiem.ts'
import { QS } from './text/qr-sapa.ts'
import { Q } from './text/quen-ma.ts'
import { T } from './text/vi.ts'
import { DaiBanMoi, Logo, ThongBao } from './ui.tsx'

/** Mã tiệm trong đường dẫn (`?tiem=`): mở từ link chủ tiệm gửi thì đó là tiệm cần vào. */
function tiemTuUrl(): string | null {
  try {
    const v = new URLSearchParams(window.location.search).get('tiem')
    return v !== null && v !== '' ? v : null
  } catch {
    return null
  }
}

/** "Nguyễn Thị Lan" → "Lan": lời chào gọi tên. Tên hai chữ ("Chị Hoa", "Anh Tú") giữ nguyên. */
function tenGoi(hoTen: string): string {
  const chu = hoTen.trim().split(/\s+/)
  return chu.length <= 2 ? chu.join(' ') : (chu.pop() ?? hoTen)
}

/** Tên thiết bị để chủ tiệm nhận ra trong danh sách phiên đang mở. */
function tenThietBi(): string {
  const w = window.screen.width
  return w >= 1024 ? 'Máy tính quầy' : w >= 700 ? 'Tablet quầy' : 'Điện thoại'
}

/** Đơn vị chủ quản / vận hành SAPA — hiện ở màn đăng nhập mọi khổ (chủ dự án yêu cầu 28/09). */
function ThongTinCongTy() {
  return (
    <footer className="dn-cong-ty">
      <p>
        <Building2 aria-hidden="true" />
        <b>{T.companyName}</b>
      </p>
      <p>
        <ReceiptText aria-hidden="true" />
        <span>{T.companyTaxShort(T.companyTaxId)}</span>
      </p>
      <p>
        <MapPin aria-hidden="true" />
        <span>{T.companyAddress.replace('TP. Hồ Chí Minh', 'TP.\u00A0Hồ\u00A0Chí\u00A0Minh')}</span>
      </p>
    </footer>
  )
}

/** Biểu tượng theo thứ tự `T.gioiThieuDiem`: trùng lịch · khách tự đặt trên Zalo · tiền rõ ràng · bật đúng thứ cần. */
const ICON_GIOI_THIEU = [CalendarCheck2, MessageCircle, ReceiptText, ToggleRight]

/** "Vì sao chọn SAPA?": 4 điểm mạnh luôn đúng cho mọi tiệm, nằm đầu vùng phụ của màn đăng nhập. */
function KhoiGioiThieu() {
  return (
    <section className="dn-gioi-thieu" aria-labelledby="gioi-thieu-tieu-de">
      <h2 id="gioi-thieu-tieu-de">{T.gioiThieuTieuDe}</h2>
      <ul>
        {T.gioiThieuDiem.map((d, i) => {
          const Icon = ICON_GIOI_THIEU[i] ?? CalendarCheck2
          return (
            <li key={d.tieuDe}>
              <Icon aria-hidden="true" />
              <div>
                <b>{d.tieuDe}</b>
                <span>{d.cau}</span>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * "SAPA đang miễn phí" thay "Giá rõ ràng" khi `SAPA_DANG_MIEN_PHI` (chủ dự án 26/10, ADR-113). Chỉ chữ: không số tiền,
 * không nút. Đổi hằng về `false` thì `KhoiGiaRo` (còn nguyên) hiện lại.
 */
function KhoiMienPhi() {
  return (
    <section className="dn-gioi-thieu dn-mien-phi" aria-labelledby="mien-phi-tieu-de">
      <h2 id="mien-phi-tieu-de">{T.mienPhiTieuDe}</h2>
      <ul>
        {T.mienPhiDong.map((d) => (
          <li key={d.tieuDe}>
            <div>
              <b>{d.tieuDe}</b>
              <span>{d.cau}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * "Giá rõ ràng" ngay dưới khối giới thiệu (chủ dự án 18/10 chiều). Số tiền / số tháng lấy từ MỘT nguồn
 * `@sapa/shared/gia-sapa`, hiển thị qua `tienVnd` (= `formatVndChuoi`, cùng đường với `GoiSapa.tsx`).
 * Chỉ giá niêm yết: KHÔNG nêu giá thử kín / miễn phí.
 */
function KhoiGiaRo() {
  const g = BANG_GIA_SAPA
  const dong = T.giaRoDong(
    tienVnd(g.giaThangNiemYetVnd.toString()),
    g.thangDongNam,
    g.thangTangKhiDongNam,
    tienVnd(tienDongNamVnd(g.giaThangNiemYetVnd).toString()),
    thangDuocDungKhiDongNam(),
  )
  return (
    <section className="dn-gioi-thieu dn-gia-ro" aria-labelledby="gia-ro-tieu-de">
      <h2 id="gia-ro-tieu-de">{T.giaRoTieuDe}</h2>
      <ul>
        {dong.map((d) => (
          <li key={d.tieuDe}>
            <div>
              <b>{d.tieuDe}</b>
              <span>{d.cau}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Nhảy thẳng tới một vùng của màn Đăng nhập rồi đưa focus vào đó (người dùng bàn phím / đọc màn hình đi tiếp từ đúng chỗ). */
function nhayToiVung(id: string) {
  const vung = document.getElementById(id)
  vung?.scrollIntoView({ block: 'start' })
  vung?.focus({ preventScroll: true })
}

/**
 * "Mở SAPA trên điện thoại": QR trang chủ cho người đứng cạnh máy quầy (docs/plans/san-pham-mien-phi-qr-ntfy.md nhóm B, ADR-114).
 * Chỉ ảnh + chữ, KHÔNG có nút (không cạnh tranh với "Vào làm việc"); nằm ở vùng phụ nên vùng chính không đổi.
 * Mã luôn là địa chỉ trang chủ (`AnhQrSapa` không nhận địa chỉ từ ngoài).
 */
function KhoiQrDangNhap() {
  return (
    <section className="dn-gioi-thieu dn-qr-sapa" aria-labelledby="dn-qr-tieu-de">
      <h2 id="dn-qr-tieu-de">{QS.dnTieuDe}</h2>
      <p>{QS.dnGoiY}</p>
      <AnhQrSapa co={180} />
    </section>
  )
}

/**
 * Vùng phụ của màn Đăng nhập (neo `#gioi-thieu`): ý → giá → QR → công ty → nút về vùng chính. Khung riêng, nền riêng,
 * không dính liền vùng chính. Chỉ vẽ khi máy chưa nhớ người.
 */
function VungPhu() {
  return (
    <section id="gioi-thieu" className="dn-vung-phu" aria-label={T.gioiThieuTieuDe} tabIndex={-1}>
      <KhoiGioiThieu />
      {SAPA_DANG_MIEN_PHI ? <KhoiMienPhi /> : <KhoiGiaRo />}
      <KhoiQrDangNhap />
      <ThongTinCongTy />
      <button
        type="button"
        className="nut nut-vien nut-rong dn-nut-vung"
        onClick={() => nhayToiVung('dang-nhap-chinh')}
      >
        <ChevronUp aria-hidden="true" />
        {T.gioiThieuVeDangNhap}
      </button>
    </section>
  )
}

function KhoiThuongHieu() {
  return (
    <aside className="dn-thuong-hieu">
      <div className="logo-chu">
        <Logo co={44} />
        {T.brand}
      </div>
      <div>
        <h2>{T.loginTagline}</h2>
        <p>{T.loginPitch}</p>
        <div className="dn-diem">
          <div>
            <Store />
            {T.loginPoint1}
          </div>
          <div>
            <CalendarCheck2 />
            {T.loginPoint2}
          </div>
          <div>
            <UserRound />
            {T.loginPoint3}
          </div>
        </div>
      </div>
      <p className="chan">{T.loginFooter}</p>
    </aside>
  )
}

/** Chip ngành ở form mở tiệm: danh mục một nguồn (ADR-082) — thứ tự chung với Cấu hình → Thông tin tiệm. */
const NGANH_MO: NganhTiem[] = nganhDeChon()

/**
 * Tự mở tiệm: 4 ô + 3 câu chọn + 2 ô đặt mã, một nút. Chủ tiệm TỰ đặt mã đăng nhập ở bước cuối rồi vào thẳng
 * bảng lịch — không còn mã "hiện một lần" hay màn Đổi mã ở lần vào đầu (ADR-087, thay cách cũ của ADR-053).
 */
function MoTiem({
  onQuayLai,
  onXong,
}: {
  /** `sdt`: số vừa dùng để mở tiệm (chỉ khi tiệm đã mở mà máy chưa vào được) — điền sẵn ở màn Đăng nhập. */
  onQuayLai: (sdt?: string) => void
  onXong: () => void
}) {
  const [tenTiem, setTenTiem] = useState('')
  // Không chọn sẵn: chưa đổi được ngành sau khi mở, chọn nhầm thì sai luôn dịch vụ mẫu và màu app khách.
  const [nganh, setNganh] = useState<NganhTiem | null>(null)
  const [tenChu, setTenChu] = useState('')
  const [sdt, setSdt] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [ma, setMa] = useState('')
  const [maLai, setMaLai] = useState('')
  /**
   * Tiệm đã mở nhưng bước đăng nhập tự động lỗi (mạng rớt giữa chừng): không cho mở lần hai, dẫn về Đăng nhập.
   * Giữ số điện thoại đã dùng để điền sẵn ở màn Đăng nhập (chủ tiệm chỉ còn gõ mã).
   */
  const [daMoChuaVao, setDaMoChuaVao] = useState<string | null>(null)
  /** Một công tắc hiện cả hai ô mã (người đứng quầy gõ nhầm 6 số hai lần là chuyện thường). */
  const [hienMa, setHienMa] = useState(false)
  /** Đã rời ô "Nhập lại mã" ít nhất một lần: từ đó mà còn lệch thì báo ngay tại ô (vòng 33 V1). */
  const [daRoiMaLai, setDaRoiMaLai] = useState(false)
  /** Bước 2: ba câu chọn mức khởi đầu (KH-GĐ2 4.1.5). Chọn sẵn = Tiệm nhỏ, bấm "Mở tiệm" luôn cũng được. */
  const [buoc, setBuoc] = useState<1 | 2>(1)
  const [soNguoi, setSoNguoi] = useState<TraLoiDangKy['soNguoi']>('1')
  const [banGoi, setBanGoi] = useState(false)
  const [hoaHong, setHoaHong] = useState(false)
  /**
   * 5.4 (ADR-094): ô tích đồng ý Điều khoản dịch vụ — chỉ hiện khi máy chủ báo cờ BẬT. Tra không được (mạng / máy chủ cũ)
   * thì coi như tắt; nếu máy chủ vẫn đòi, câu lỗi của nó (`CAU_CAN_DONG_Y_DIEU_KHOAN`) bật ô lên.
   */
  const [dieuKhoan, setDieuKhoan] = useState<{ bat: boolean; duongDan: string } | null>(null)
  const [dongY, setDongY] = useState(false)
  const [thieuDongY, setThieuDongY] = useState(false)
  useEffect(() => {
    let con = true
    auth
      .dieuKhoanCuaHang()
      .then((r) => {
        if (con) setDieuKhoan({ bat: r.bat, duongDan: r.duongDan ?? DUONG_DAN_DIEU_KHOAN_CUA_HANG })
      })
      .catch(() => undefined)
    return () => {
      con = false
    }
  }, [])
  const canDongY = dieuKhoan?.bat === true

  async function mo(e: React.FormEvent) {
    e.preventDefault()
    if (buoc === 1) {
      if (!hopLe) return
      setLoi(null)
      setBuoc(2)
      return
    }
    const loiMa = loiMaMoTiem(ma, maLai)
    if (loiMa !== null) {
      // Lệch mã: câu báo đã nằm ngay dưới ô "Nhập lại mã" — không lặp thêm hộp lỗi ở đáy.
      if (loiMa === T.pinMismatch) setDaRoiMaLai(true)
      else setLoi(loiMa)
      return
    }
    if (canDongY && !dongY) {
      setThieuDongY(true)
      return
    }
    setDang(true)
    setLoi(null)
    let tiem: string
    let sdtChuan: string
    try {
      const kq = await auth.moTiem({
        tenTiem: tenTiem.trim(),
        nganh: nganh ?? 'khac',
        tenChu: tenChu.trim(),
        sdt: sdt.trim(),
        ma,
        // Một mình thì không có ai để trả hoa hồng — câu 3 ẩn, gửi "không".
        traLoi: { soNguoi, banGoi, hoaHong: soNguoi !== '1' && hoaHong },
        ...(canDongY ? { dongYDieuKhoan: true as const } : {}),
      })
      tiem = kq.tiem
      sdtChuan = kq.sdt
    } catch (err) {
      // Máy chủ chê số điện thoại (hiếm: máy đã kiểm cùng luật): về bước 1, nói rõ ô nào sai.
      if (laLoiSdtMayChu(err)) {
        setBuoc(1)
        setLoi(E.sdtSai)
      } else if (err instanceof ApiFailure && err.messageVi === CAU_CAN_DONG_Y_DIEU_KHOAN) {
        // Máy chủ đòi đồng ý mà máy chưa biết cờ bật: hiện ô tích để chủ tiệm đọc và đồng ý.
        setDieuKhoan({ bat: true, duongDan: DUONG_DAN_DIEU_KHOAN_CUA_HANG })
        setThieuDongY(true)
      } else {
        setLoi(err instanceof ApiFailure ? err.messageVi : T.serverFailed)
      }
      setDang(false)
      return
    }
    // Tiệm đã mở: vào thẳng bằng đúng mã vừa đặt (đường đăng nhập thật, không đường tắt).
    try {
      const dn = await auth.dangNhap({ tiem, sdt: sdtChuan, ma, thietBi: tenThietBi() })
      if (!coToken(dn)) throw new Error('chon-tiem')
      luuToken(dn.token)
      ghiNho({
        tiem: dn.tiem?.id ?? tiem,
        sdt: sdtChuan,
        ten: dn.toi.ten,
        ...(dn.tiem !== undefined ? { tenTiem: dn.tiem.ten } : {}),
      })
      ghiVuaMoTiem()
      onXong()
    } catch {
      setDaMoChuaVao(hienSdt(sdtChuan) || sdtChuan)
      setLoi(null)
    } finally {
      setDang(false)
    }
  }

  if (daMoChuaVao !== null) {
    return (
      <div className="dn-form">
        <div className="logo-dien-thoai">
          <Logo co={36} />
          {T.brand}
        </div>
        <ThongBao loai="canh-bao" lon>
          {E.daMoChuaVao}
        </ThongBao>
        <button
          type="button"
          className="nut nut-chinh nut-lon nut-rong"
          onClick={() => onQuayLai(daMoChuaVao)}
        >
          {E.vaoDangNhap}
        </button>
      </div>
    )
  }

  /**
   * Nút "Tiếp" khoá thì nói VÌ SAO ngay dưới nút (ui-critic 01/10). Số điện thoại sai thì câu báo nằm ngay
   * dưới ô số điện thoại, không lặp lại dưới nút.
   */
  const lyDoKhoa = lyDoChuaTiep({ tenTiem, nganh, tenChu, sdt })
  const hopLe = lyDoKhoa === null
  const sdtSai = sdt.trim() !== '' && !sdtMoTiemHopLe(sdt)

  const loiLech = loiMaLechSom(ma, maLai, daRoiMaLai)

  if (buoc === 2) {
    return (
      <form className="dn-form" onSubmit={mo}>
        <div className="logo-dien-thoai">
          <Logo co={36} />
          {T.brand}
        </div>
        <div>
          <p className="chi-bao-buoc">{E.buoc(2)}</p>
          <h1>{E.buoc2TieuDe}</h1>
          <p className="loi-chao">{E.buoc2GoiY}</p>
        </div>
        <ChonMot
          nhan={E.soNguoi}
          chon={soNguoi}
          cac={[
            ['1', E.motMinh],
            ['2-5', E.haiDenNam],
            ['6+', E.trenNam],
          ]}
          onChon={setSoNguoi}
        />
        <ChonMot
          nhan={E.banGoi}
          chon={banGoi ? 'co' : 'khong'}
          cac={[
            ['co', E.co],
            ['khong', E.khong],
          ]}
          onChon={(x) => setBanGoi(x === 'co')}
        />
        {soNguoi !== '1' && (
          <ChonMot
            nhan={E.hoaHong}
            chon={hoaHong ? 'co' : 'khong'}
            cac={[
              ['co', E.co],
              ['khong', E.khong],
            ]}
            onChon={(x) => setHoaHong(x === 'co')}
          />
        )}
        <div className="khoi-ma">
          <h2>{E.maTieuDe}</h2>
          <p className="goi-y-ma">{E.maGhiGiay}</p>
        </div>
        <div className="truong">
          <label className="nhan" htmlFor="mo-ma">
            {E.datMa}
          </label>
          <div className="o-nhap-icon">
            <KeyRound size={20} />
            <input
              id="mo-ma"
              className="o-nhap"
              type={hienMa ? 'text' : 'password'}
              value={ma}
              maxLength={6}
              inputMode="numeric"
              autoComplete="new-password"
              placeholder={T.pinPlaceholder}
              onChange={(e) => setMa(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>
        <div className="truong">
          <label className="nhan" htmlFor="mo-ma-lai">
            {E.nhapLaiMa}
          </label>
          <div className="o-nhap-icon">
            <KeyRound size={20} />
            <input
              id="mo-ma-lai"
              className={`o-nhap ${loiLech !== null ? 'o-sai' : ''}`}
              type={hienMa ? 'text' : 'password'}
              value={maLai}
              maxLength={6}
              inputMode="numeric"
              autoComplete="new-password"
              placeholder={T.pinPlaceholder}
              aria-invalid={loiLech !== null}
              onChange={(e) => setMaLai(e.target.value.replace(/\D/g, ''))}
              onBlur={() => setDaRoiMaLai(true)}
            />
          </div>
          {loiLech !== null ? (
            <p className="chu-loi" role="alert">
              {loiLech}
            </p>
          ) : (
            <p className="goi-y-ma">{E.maLanSau}</p>
          )}
          {/* Liên kết nhỏ, không phải nút phụ rộng hết hàng: việc phụ không tranh với "Mở tiệm". */}
          <button
            type="button"
            className="nut-lien-ket"
            aria-pressed={hienMa}
            onClick={() => setHienMa((h) => !h)}
          >
            {hienMa ? <EyeOff size={18} /> : <Eye size={18} />}
            {hienMa ? E.anMa : E.hienMa}
          </button>
        </div>
        {canDongY && (
          <div className="dong-y-dk">
            <label className="dong-y-dk-nhan">
              <input
                type="checkbox"
                checked={dongY}
                onChange={(e) => {
                  setDongY(e.target.checked)
                  setThieuDongY(false)
                }}
              />
              <span>{E.dongYDieuKhoan}</span>
            </label>
            <a
              className="nut-lien-ket"
              href={duongDanMayChu(dieuKhoan?.duongDan ?? DUONG_DAN_DIEU_KHOAN_CUA_HANG)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {E.docDieuKhoan}
            </a>
            {thieuDongY && (
              <p className="chu-loi" role="alert">
                {E.canDongYDieuKhoan}
              </p>
            )}
          </div>
        )}
        {loi !== null && (
          <ThongBao loai="loi" lon>
            {loi}
          </ThongBao>
        )}
        <button type="submit" className="nut nut-chinh nut-lon nut-rong" disabled={dang}>
          {dang ? E.dangMo : E.moNut}
        </button>
        <button
          type="button"
          className="nut nut-nhat nut-lon nut-rong"
          onClick={() => {
            setLoi(null)
            setBuoc(1)
          }}
        >
          {E.quayLai}
        </button>
      </form>
    )
  }

  return (
    <form className="dn-form" onSubmit={mo}>
      <div className="logo-dien-thoai">
        <Logo co={36} />
        {T.brand}
      </div>
      <div>
        <p className="chi-bao-buoc">{E.buoc(1)}</p>
        <h1>{E.tieuDe}</h1>
        <p className="loi-chao">{E.goiY}</p>
      </div>
      <div className="truong">
        <label className="nhan" htmlFor="mo-ten-tiem">
          {E.tenTiem}
        </label>
        <input
          id="mo-ten-tiem"
          className="o-nhap"
          value={tenTiem}
          maxLength={120}
          placeholder={E.tenTiemGoiY}
          onChange={(e) => setTenTiem(e.target.value)}
        />
      </div>
      <div className="truong">
        <span className="nhan">{E.nganh}</span>
        <div className="hang-chip luoi-chip-2">
          {NGANH_MO.map((n) => (
            <button
              key={n}
              type="button"
              className={`chip ${nganh === n ? 'dang-chon' : ''}`}
              aria-pressed={nganh === n}
              onClick={() => setNganh(n)}
            >
              {TEN_NGANH[n]}
            </button>
          ))}
        </div>
      </div>
      <div className="truong">
        <label className="nhan" htmlFor="mo-ten-chu">
          {E.tenChu}
        </label>
        <input
          id="mo-ten-chu"
          className="o-nhap"
          value={tenChu}
          maxLength={120}
          autoComplete="name"
          onChange={(e) => setTenChu(e.target.value)}
        />
      </div>
      <div className="truong">
        <label className="nhan" htmlFor="mo-sdt">
          {T.loginPhoneLabel}
        </label>
        <input
          id="mo-sdt"
          className="o-nhap"
          value={sdt}
          inputMode="tel"
          // "username": trình duyệt gợi ý lưu cặp số điện thoại + mã sau khi mở tiệm.
          autoComplete="username"
          placeholder={T.phonePlaceholder}
          aria-invalid={sdtSai}
          onChange={(e) => {
            setSdt(e.target.value)
            setLoi(null)
          }}
        />
        {sdtSai && <p className="chu-loi">{E.sdtSai}</p>}
      </div>
      {loi !== null && (
        <ThongBao loai="loi" lon>
          {loi}
        </ThongBao>
      )}
      <button type="submit" className="nut nut-chinh nut-lon nut-rong" disabled={!hopLe || dang}>
        {E.tiepNut}
      </button>
      {lyDoKhoa !== null && lyDoKhoa !== E.sdtSai && <p className="goi-y-o">{lyDoKhoa}</p>}
      <button type="button" className="nut nut-nhat nut-lon nut-rong" onClick={() => onQuayLai()}>
        {E.coTiemRoi}
      </button>
    </form>
  )
}

/** Một câu hỏi, vài chip chọn một (bước 2 của mở tiệm). */
function ChonMot<M extends string>({
  nhan,
  chon,
  cac,
  onChon,
}: {
  nhan: string
  chon: M
  cac: readonly (readonly [M, string])[]
  onChon: (m: M) => void
}) {
  return (
    <div className="truong">
      <span className="nhan">{nhan}</span>
      <div className={`hang-chip chia-deu cot-${cac.length}`}>
        {cac.map(([ma, ten]) => (
          <button
            key={ma}
            type="button"
            className={`chip ${chon === ma ? 'dang-chon' : ''}`}
            aria-pressed={chon === ma}
            onClick={() => onChon(ma)}
          >
            {ten}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Kết quả đăng nhập có token (không phải "chọn tiệm"). */
function coToken(kq: KetQuaDangNhap): kq is Extract<KetQuaDangNhap, { token: string }> {
  return 'token' in kq
}

/**
 * Chủ tiệm tự lấy lại mã bằng mã gửi qua Zalo (ADR-072): số → mã 6 số → mã mới → về đăng nhập (không tự vào).
 * Mỗi bước một ô, một nút chính.
 */
function LayLaiMaZalo({
  linkZalo,
  onXong,
}: {
  linkZalo: string | null
  onXong: (sdt: string) => void
}) {
  const [buoc, setBuoc] = useState<'sdt' | 'ma' | 'ma-moi' | 'xong'>('sdt')
  const [sdt, setSdt] = useState('')
  const [ma, setMa] = useState('')
  const [phieu, setPhieu] = useState<{
    phieu: string
    tiem: string[]
    dsTiem: TiemDatMa[]
  } | null>(null)
  /** SEC-6: các tiệm người dùng tích chọn để đặt lại mã (chỉ dùng khi số là chủ ≥ 2 tiệm). */
  const [chonTiem, setChonTiem] = useState<string[]>([])
  /** Số tiệm mang số này mà KHÔNG được đặt lại (server báo) — nói rõ ở màn xong. */
  const [conLai, setConLai] = useState(0)
  const [moi, setMoi] = useState('')
  const [moiLai, setMoiLai] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [conGiay, setConGiay] = useState(0)

  useEffect(() => {
    if (conGiay <= 0) return
    const t = setTimeout(() => setConGiay((g) => g - 1), 1000)
    return () => clearTimeout(t)
  }, [conGiay])

  async function lam(f: () => Promise<void>) {
    setLoi(null)
    setDang(true)
    try {
      await f()
    } catch (err) {
      setLoi(err instanceof ApiFailure ? err.messageVi : T.serverFailed)
    } finally {
      setDang(false)
    }
  }

  /** Số là chủ từ 2 tiệm trở lên → bắt buộc chọn tiệm cần đặt lại (SEC-6). Một tiệm → giao diện như cũ. */
  const nhieuTiem = (phieu?.dsTiem.length ?? 0) >= 2

  const guiMa = () =>
    lam(async () => {
      await auth.quenMaGui(sdt.trim())
      setMa('')
      setConGiay(60)
      setBuoc('ma')
    })

  const oSo = (id: string, nhan: string, giaTri: string, doi: (v: string) => void) => (
    <div className="truong">
      <label className="nhan" htmlFor={id}>
        {nhan}
      </label>
      <div className="o-nhap-icon">
        <KeyRound size={20} />
        <input
          id={id}
          className="o-nhap"
          type="password"
          value={giaTri}
          onChange={(e) => doi(e.target.value)}
          placeholder={T.pinPlaceholder}
          inputMode="numeric"
          autoComplete="new-password"
          required
        />
      </div>
    </div>
  )

  return (
    <>
      {buoc === 'sdt' && (
        <form
          className="dn-form-con"
          onSubmit={(e) => {
            e.preventDefault()
            void guiMa()
          }}
        >
          <div>
            <h1>{Q.zaloTieuDe}</h1>
            <p className="loi-chao">{Q.zaloGoiY}</p>
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="qm-sdt">
              {T.loginPhoneLabel}
            </label>
            <div className="o-nhap-icon">
              <Phone size={20} />
              <input
                id="qm-sdt"
                className="o-nhap"
                value={sdt}
                onChange={(e) => setSdt(e.target.value)}
                placeholder={T.phonePlaceholder}
                inputMode="tel"
                autoComplete="tel"
                required
              />
            </div>
          </div>
          {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          <button type="submit" className="nut nut-chinh nut-lon nut-rong" disabled={dang}>
            <MessageCircle size={20} />
            {dang ? Q.dangGui : Q.guiMa}
          </button>
        </form>
      )}
      {buoc === 'ma' && (
        <form
          className="dn-form-con"
          onSubmit={(e) => {
            e.preventDefault()
            void lam(async () => {
              const p = await auth.quenMaXacNhan(sdt.trim(), ma.trim())
              setPhieu(p)
              // KHÔNG chọn sẵn tiệm nào — trừ tiệm mà máy này đã nhớ (máy nhớ id tiệm, máy cũ nhớ mã tiệm).
              const nho = docNho()
              setChonTiem(
                p.dsTiem.length >= 2 && nho !== null
                  ? p.dsTiem
                      .filter((t) => t.id === nho.tiem || t.maTiem === nho.tiem)
                      .map((t) => t.id)
                  : [],
              )
              setBuoc('ma-moi')
            })
          }}
        >
          <div>
            <h1>{Q.nhapMaTieuDe}</h1>
            <p className="loi-chao">{Q.daGui(sdt.trim())}</p>
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="qm-ma">
              {Q.oMa}
            </label>
            <input
              id="qm-ma"
              className="o-nhap"
              value={ma}
              onChange={(e) => setMa(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
          </div>
          {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          <button
            type="submit"
            className="nut nut-chinh nut-lon nut-rong"
            disabled={dang || ma.length !== 6}
          >
            {Q.xacNhan}
          </button>
          <div className="hang-nut">
            <button
              type="button"
              className="nut nut-vien"
              disabled={dang || conGiay > 0}
              onClick={() => void guiMa()}
            >
              {conGiay > 0 ? Q.guiLaiSau(conGiay) : Q.guiLai}
            </button>
            <button type="button" className="nut nut-nhat" onClick={() => setBuoc('sdt')}>
              {Q.doiSoKhac}
            </button>
          </div>
        </form>
      )}
      {buoc === 'ma-moi' && phieu !== null && (
        <form
          className="dn-form-con"
          onSubmit={(e) => {
            e.preventDefault()
            if (moi !== moiLai) {
              setLoi(Q.maKhongKhop)
              return
            }
            void lam(async () => {
              const kq = await auth.quenMaDatMa(phieu.phieu, moi, nhieuTiem ? chonTiem : undefined)
              setConLai(kq.conLai ?? 0)
              setBuoc('xong')
            })
          }}
        >
          <div>
            <h1>{nhieuTiem ? Q.datMaChonTieuDe : Q.maMoiTieuDe}</h1>
            {nhieuTiem ? (
              <p className="loi-chao">{Q.datMaChonGoiY}</p>
            ) : (
              <p className="loi-chao">{Q.maMoiChoTiem(phieu.tiem.join(', '))}</p>
            )}
          </div>
          {nhieuTiem && (
            <div className="ds-tinh-nang">
              {phieu.dsTiem.map((t) => (
                <label key={t.id} className="cong-tac-dong tinh-nang-dong">
                  <span className="chu">
                    <b>{t.ten}</b>
                    <span>{Q.datMaMaTiem(t.maTiem)}</span>
                    <span>{Q.datMaMoNgay(t.moNgay)}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={chonTiem.includes(t.id)}
                    onChange={(e) =>
                      setChonTiem((ds) =>
                        e.target.checked ? [...ds, t.id] : ds.filter((x) => x !== t.id),
                      )
                    }
                  />
                </label>
              ))}
            </div>
          )}
          {oSo('qm-moi', Q.oMaMoi, moi, setMoi)}
          {oSo('qm-moi-lai', Q.oMaMoiLai, moiLai, setMoiLai)}
          {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          <button
            type="submit"
            className="nut nut-chinh nut-lon nut-rong"
            disabled={dang || (nhieuTiem && chonTiem.length === 0)}
          >
            {Q.luuMaMoi}
          </button>
        </form>
      )}
      {buoc === 'xong' && (
        <>
          <div>
            <h1>{Q.xongTieuDe}</h1>
            <p className="loi-chao">{conLai > 0 ? Q.xongGoiYMotPhan : Q.xongGoiY}</p>
            {conLai > 0 && <p className="loi-chao">{Q.conTiemGiuMaCu(conLai)}</p>}
          </div>
          <button
            type="button"
            className="nut nut-chinh nut-lon nut-rong"
            onClick={() => onXong(sdt.trim())}
          >
            {Q.xongNut}
          </button>
        </>
      )}
      {(buoc === 'sdt' || buoc === 'ma') && linkZalo !== null && (
        <a
          className="nut nut-nhat nut-lon nut-rong"
          href={linkZalo}
          target="_blank"
          rel="noreferrer"
        >
          {Q.khongNhanDuoc}
        </a>
      )}
    </>
  )
}

/**
 * "Quên mã?": nhân viên → nhờ chủ tiệm; chủ tiệm → lấy lại mã qua Zalo (khi máy chủ bật), không thì nhắn / gọi
 * SAPA.
 */
function QuenMa({ onQuayLai, onXong }: { onQuayLai: () => void; onXong: (sdt: string) => void }) {
  const [vai, setVai] = useState<'nhan-vien' | 'chu' | null>(null)
  const [hoTro, setHoTro] = useState<HoTro | null>(null)

  useEffect(() => {
    let dung = false
    auth
      .hoTro()
      .then((h) => {
        if (!dung) setHoTro(h)
      })
      .catch(() => {
        if (!dung) setHoTro({ sdt: null, otp: false })
      })
    return () => {
      dung = true
    }
  }, [])

  const soGon = hoTro?.sdt?.replace(/\D/g, '') ?? ''
  const linkZalo = hoTro?.zalo ?? (soGon === '' ? null : `https://zalo.me/${soGon}`)
  return (
    <div className="dn-form">
      <div className="logo-dien-thoai">
        <Logo co={36} />
        {T.brand}
      </div>
      {vai === null && (
        <>
          <div>
            <h1>{Q.tieuDe}</h1>
            <p className="loi-chao">{Q.hoiVai}</p>
          </div>
          <button
            type="button"
            className="nut nut-vien nut-lon nut-rong"
            onClick={() => setVai('nhan-vien')}
          >
            <UserRound size={20} />
            {Q.laNhanVien}
          </button>
          <button
            type="button"
            className="nut nut-vien nut-lon nut-rong"
            onClick={() => setVai('chu')}
          >
            <Store size={20} />
            {Q.laChu}
          </button>
        </>
      )}
      {vai === 'nhan-vien' && (
        <>
          <h1>{Q.nhanVienTieuDe}</h1>
          <ol className="cac-buoc">
            {Q.nhanVienBuoc.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ol>
          <button type="button" className="nut nut-chinh nut-lon nut-rong" onClick={onQuayLai}>
            {Q.nhanVienXong}
          </button>
        </>
      )}
      {vai === 'chu' && hoTro?.otp === true && <LayLaiMaZalo linkZalo={linkZalo} onXong={onXong} />}
      {vai === 'chu' && hoTro?.otp !== true && (
        <>
          <div>
            <h1>{Q.chuTieuDe}</h1>
            <p className="loi-chao">{Q.chuCach}</p>
          </div>
          {hoTro !== null && (linkZalo !== null || soGon !== '') ? (
            <>
              {linkZalo !== null && (
                <a
                  className="nut nut-chinh nut-lon nut-rong"
                  href={linkZalo}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle size={20} />
                  {Q.nhanZalo}
                </a>
              )}
              {soGon !== '' && (
                <a className="nut nut-vien nut-rong" href={`tel:${soGon}`}>
                  <Phone size={20} />
                  {Q.goi(hoTro.sdt ?? '')}
                </a>
              )}
              <p className="goi-y-nho">{Q.chuDoiSo}</p>
            </>
          ) : (
            hoTro !== null && <ThongBao loai="canh-bao">{Q.chuaCoSo}</ThongBao>
          )}
        </>
      )}
      {vai !== 'nhan-vien' && (
        <button type="button" className="nut nut-nhat nut-lon nut-rong" onClick={onQuayLai}>
          {Q.quayLai}
        </button>
      )}
    </div>
  )
}

export function DangNhap({ onXong, hetPhien }: { onXong: () => void; hetPhien: boolean }) {
  const [man, setMan] = useState<'dang-nhap' | 'mo-tiem' | 'quen-ma'>('dang-nhap')
  /** Máy đã nhớ người này: chỉ còn hỏi mã. `null` = hỏi đủ. Mở từ link `?tiem=` thì hỏi đủ. */
  const [nho, setNho] = useState<DaNho | null>(() => (tiemTuUrl() === null ? docNho() : null))
  const [sdt, setSdt] = useState('')
  const [ma, setMa] = useState('')
  const [dangGui, setDangGui] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  /** Số này làm ở nhiều tiệm cùng một mã: chọn tiệm rồi gửi lại. */
  const [chonTiem, setChonTiem] = useState<TiemDeChon[] | null>(null)
  const [dangVaoTiem, setDangVaoTiem] = useState<string | null>(null)
  const oMa = useRef<HTMLInputElement>(null)

  // Mở `/#gioi-thieu`: nhảy thẳng tới vùng phụ (nếu màn này đang có vùng đó). Không cuộn mượt.
  useEffect(() => {
    if (window.location.hash === '#gioi-thieu')
      document.getElementById('gioi-thieu')?.scrollIntoView({ block: 'start' })
  }, [])

  async function vao(tiemChon: string | null) {
    // Tiệm chỉ định: tiệm vừa chọn → máy đã nhớ → link `?tiem=`. Không có thì máy chủ tự tìm theo số.
    const url = tiemTuUrl()
    const tiem = tiemChon ?? nho?.tiem ?? (url === null ? '' : chuanHoaMaTiem(url))
    const so = nho?.sdt ?? sdt.trim()
    setLoi(null)
    setDangGui(true)
    setDangVaoTiem(tiemChon)
    try {
      const kq = await auth.dangNhap({
        ...(tiem === '' ? {} : { tiem }),
        sdt: so,
        ma,
        thietBi: tenThietBi(),
      })
      if (!coToken(kq)) {
        setChonTiem(kq.chonTiem)
        return
      }
      luuToken(kq.token)
      const tenTiem = kq.tiem?.ten ?? chonTiem?.find((t) => t.id === tiemChon)?.ten ?? nho?.tenTiem
      ghiNho({
        tiem: kq.tiem?.id ?? tiem,
        sdt: so,
        ten: kq.toi.ten,
        ...(tenTiem !== undefined && tenTiem !== '' ? { tenTiem } : {}),
      })
      onXong()
    } catch (err) {
      setChonTiem(null)
      setLoi(err instanceof ApiFailure ? err.messageVi : T.serverFailed)
      setMa('')
      oMa.current?.focus()
    } finally {
      setDangGui(false)
    }
  }

  function gui(e: React.FormEvent) {
    e.preventDefault()
    void vao(null)
  }

  /** "Không phải bạn?": quên người đang nhớ, về form đủ (số điện thoại + mã). */
  function khongPhaiToi() {
    quenNho()
    setNho(null)
    setMa('')
    setLoi(null)
  }

  if (man !== 'dang-nhap') {
    return (
      <div className="man-dang-nhap">
        <KhoiThuongHieu />
        <div className="dn-form-khung">
          {man === 'mo-tiem' && (
            <MoTiem
              onQuayLai={(so) => {
                // Tiệm đã mở mà máy chưa vào được: điền sẵn số vừa dùng, chủ tiệm chỉ còn gõ mã.
                if (so !== undefined) setSdt(so)
                setMan('dang-nhap')
              }}
              onXong={onXong}
            />
          )}
          {man === 'quen-ma' && (
            <QuenMa
              onQuayLai={() => setMan('dang-nhap')}
              onXong={(so) => {
                // Vừa đặt mã mới: về đăng nhập, điền sẵn số; máy đang nhớ người khác thì hỏi đủ.
                const cungNguoi =
                  nho !== null && nho.sdt.replace(/\D/g, '') === so.replace(/\D/g, '')
                if (!cungNguoi) {
                  if (nho !== null) quenNho()
                  setNho(null)
                  setSdt(so)
                }
                setMa('')
                setLoi(null)
                setMan('dang-nhap')
              }}
            />
          )}
          <DaiBanMoi />
          <ThongTinCongTy />
        </div>
      </div>
    )
  }

  if (chonTiem !== null) {
    return (
      <div className="man-dang-nhap">
        <KhoiThuongHieu />
        <div className="dn-form-khung">
          <div className="dn-form">
            <div className="logo-dien-thoai">
              <Logo co={36} />
              {T.brand}
            </div>
            <div>
              <h1>{Q.chonTiemTieuDe}</h1>
              <p className="loi-chao">{Q.chonTiemGoiY}</p>
            </div>
            {chonTiem.map((t) => (
              <button
                key={t.id}
                type="button"
                className="nut nut-vien nut-rong nut-chon-tiem"
                disabled={dangGui}
                onClick={() => void vao(t.id)}
              >
                <Store size={20} />
                <span>
                  <b>{t.ten}</b>
                  <small>
                    {dangVaoTiem === t.id ? Q.dangVao : Q.dongPhuTiem(t.tenVai ?? '', t.ma ?? '')}
                  </small>
                </span>
              </button>
            ))}
            <button
              type="button"
              className="nut nut-nhat nut-lon nut-rong"
              onClick={() => {
                setChonTiem(null)
                setMa('')
              }}
            >
              {Q.quayLai}
            </button>
          </div>
          <DaiBanMoi />
          <ThongTinCongTy />
        </div>
      </div>
    )
  }

  const oNhapMa = (
    <div className="truong">
      <label className="nhan" htmlFor="ma">
        {T.pinLabel}
      </label>
      <div className="o-nhap-icon">
        <KeyRound size={20} />
        <input
          ref={oMa}
          id="ma"
          className="o-nhap"
          type="password"
          value={ma}
          onChange={(e) => setMa(e.target.value)}
          placeholder={T.pinPlaceholder}
          inputMode="numeric"
          autoComplete="current-password"
          // Máy đã nhớ người: ô mã là việc duy nhất còn lại, mở sẵn bàn phím.
          // biome-ignore lint/a11y/noAutofocus: màn chỉ có đúng một ô để gõ.
          autoFocus={nho !== null}
          required
        />
      </div>
    </div>
  )

  const bieuMau = (
    <form className="dn-form" onSubmit={gui}>
      <div className="logo-dien-thoai">
        <Logo co={36} />
        {T.brand}
      </div>
      {/* Điện thoại: khối giới thiệu bên trái bị ẩn, nên nói một câu đây là phần mềm gì (vòng 33 V2). */}
      <p className="dn-mo-ta-dt">{T.loginShortPitch}</p>

      {nho !== null && (
        <>
          <div>
            <h1>{T.loginWelcomeName(tenGoi(nho.ten))}</h1>
            <p className="loi-chao">{T.loginPinOnly}</p>
          </div>
          {hetPhien && <ThongBao loai="canh-bao">{T.sessionEnded}</ThongBao>}
          <div className="the-nguoi-nho">
            <Store size={20} />
            <div>
              <b>{nho.tenTiem ?? T.brand}</b>
              <span>{hienSdt(nho.sdt) || nho.sdt}</span>
            </div>
            <button type="button" className="nut nut-nhat nut-nho" onClick={khongPhaiToi}>
              {T.notMe}
            </button>
          </div>
          {oNhapMa}
        </>
      )}
      {nho === null && (
        <>
          <div>
            <h1>{T.loginTitle}</h1>
            <p className="loi-chao">{T.loginHint}</p>
          </div>
          {hetPhien && <ThongBao loai="canh-bao">{T.sessionEnded}</ThongBao>}

          <div className="truong">
            <label className="nhan" htmlFor="sdt">
              {T.loginPhoneLabel}
            </label>
            <div className="o-nhap-icon">
              <Phone size={20} />
              <input
                id="sdt"
                className="o-nhap"
                value={sdt}
                onChange={(e) => setSdt(e.target.value)}
                placeholder={T.phonePlaceholder}
                inputMode="tel"
                autoComplete="username"
                required
              />
            </div>
          </div>

          {oNhapMa}
        </>
      )}

      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}

      <button type="submit" className="nut nut-chinh nut-lon nut-rong" disabled={dangGui}>
        {dangGui ? T.loggingIn : T.loginButton}
      </button>
      {/* Người mới phải thấy lối "Mở tiệm" ngay dưới nút chính (kế hoạch 10/2026, 1.6); "Quên mã" xuống cuối. */}
      {nho === null && (
        <button
          type="button"
          className="nut nut-vien nut-lon nut-rong"
          onClick={() => setMan('mo-tiem')}
        >
          {E.chuaCoTiem}
        </button>
      )}
      <button
        type="button"
        className="nut nut-nhat nut-lon nut-rong"
        onClick={() => setMan('quen-ma')}
      >
        {Q.nutQuen}
      </button>
    </form>
  )

  return nho === null ? (
    <div className="man-dang-nhap">
      <KhoiThuongHieu />
      <div className="dn-form-khung co-gioi-thieu">
        <div id="dang-nhap-chinh" className="dn-vung-chinh" tabIndex={-1}>
          {bieuMau}
          <DaiBanMoi />
          <button
            type="button"
            className="nut nut-vien nut-rong dn-nut-vung"
            aria-controls="gioi-thieu"
            onClick={() => nhayToiVung('gioi-thieu')}
          >
            {T.gioiThieuMoNut}
            <ChevronDown aria-hidden="true" />
          </button>
        </div>
        <VungPhu />
      </div>
    </div>
  ) : (
    <div className="man-dang-nhap">
      <KhoiThuongHieu />
      <div className="dn-form-khung">
        {bieuMau}
        <DaiBanMoi />
        <ThongTinCongTy />
      </div>
    </div>
  )
}

/**
 * Đổi mã — hiện ra khi mã hiện tại do chủ tiệm đặt hộ.
 *
 * Không phải một lời nhắc có thể bỏ qua: máy chủ **chặn mọi việc khác** cho tới khi đổi xong, vì
 * mã tạm là mã chủ tiệm biết, và làm việc bằng mã đó thì nhật ký ghi tên nhân viên cho thao tác
 * của người khác.
 */
export function DoiMa({ onXong }: { onXong: () => void }) {
  return (
    <div className="man-dang-nhap">
      <KhoiThuongHieu />
      <div className="dn-form-khung">
        <FormDoiMa
          onXong={onXong}
          dau={
            <>
              <div className="logo-dien-thoai">
                <Logo co={36} />
                {T.brand}
              </div>
              <div>
                <h1>{T.changePinTitle}</h1>
                <p className="loi-chao">{T.changePinWhy}</p>
              </div>
            </>
          }
        />
        <ThongTinCongTy />
      </div>
    </div>
  )
}
